
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Newspaper, 
  BrainCircuit, 
  ArrowUpRight, 
  Clock
} from 'lucide-react';

export interface NewsItem {
  id: string;
  title: string;
  category: 'CRITICAL ALERT' | 'AI INSIGHT' | 'MARKET SHIFT' | 'SECURITY PATCH';
  summary: string;
  content: string;
  date: string;
}

export interface EventItem {
  id: string;
  title: string;
  date: string;
  time: string;
  link: string;
}

const INITIAL_NEWS: NewsItem[] = [
  {
    id: '1',
    title: 'Quantum Decryption Vulnerability Detected in Legacy Chains',
    category: 'CRITICAL ALERT',
    summary: 'Astra Defensive Matrices have identified a mathematical bypass affecting RSA-2048 encryption protocols.',
    content: 'Full intelligence breakdown suggests immediate migration to Post-Quantum Cryptographic layers. Sentrix Astra customers are being automatically transitioned to v5.1 keys.',
    date: '2025-05-14'
  },
  {
    id: '2',
    title: 'Autonomous Neural Firewalls: The v4.0 Breakthrough',
    category: 'AI INSIGHT',
    summary: 'A new architecture for self-healing security kernels has passed stress testing in the Nebula Network.',
    content: 'By utilizing sub-millisecond weight adjustments, our neural firewalls now anticipate breach attempts before they are executed.',
    date: '2025-05-12'
  },
  {
    id: '3',
    title: 'Global Cybersecurity Policy Shift: Implications for NGO Infrastructure',
    category: 'MARKET SHIFT',
    summary: 'New UN directives mandate high-integrity digital citadels for global humanitarian organizations.',
    content: 'Sentrix Astra is partnering with key foundations to deploy secure web portals compliant with the latest sovereignty laws.',
    date: '2025-05-10'
  }
];

const INITIAL_EVENTS: EventItem[] = [
  {
    id: '1',
    title: 'The Future of Neural Defense Webinar',
    date: 'June 12, 2025',
    time: '14:00 UTC',
    link: '#'
  },
  {
    id: '2',
    title: 'Zero Trust Architecture Workshop',
    date: 'June 20, 2025',
    time: '09:00 UTC',
    link: '#'
  },
  {
    id: '3',
    title: 'Cyber-Resilience Summit 2025',
    date: 'July 05, 2025',
    time: 'Full Day',
    link: '#'
  }
];

const IntelligenceHub: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);

  useEffect(() => {
    // Load data from LocalStorage or use defaults
    const savedNews = localStorage.getItem('astra_news');
    const savedEvents = localStorage.getItem('astra_events');

    setNews(savedNews ? JSON.parse(savedNews) : INITIAL_NEWS);
    setEvents(savedEvents ? JSON.parse(savedEvents) : INITIAL_EVENTS);
  }, []);

  const handleRegisterInterest = (eventTitle: string) => {
    const confirmed = window.confirm(`Confirm registration interest for: "${eventTitle}"?`);
    if (confirmed) {
      console.log(`[ASTRA_LOG] User registered interest for event: ${eventTitle} at ${new Date().toISOString()}`);
      alert('INTEREST_LOGGED: Our team will contact you via secure channel.');
    }
  };

  return (
    <div className="pt-32 pb-40 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-start mb-24 space-y-8"
        >
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-brand-500/10 border border-brand-500/20 backdrop-blur-3xl glint">
            <BrainCircuit className="w-4 h-4 text-brand-400" />
            <span className="text-[10px] font-black text-brand-300 uppercase tracking-[0.4em] font-mono">
              TACTICAL INTEL & GLOBAL EVENTS
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter leading-none">
            Intelligence <span className="premium-gradient">Hub</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl font-light leading-relaxed">
            Real-time strategic briefings and educational symposiums for the modern defense architect.
          </p>
        </motion.div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-16">
          
          {/* News Feed (2/3 width) */}
          <div className="lg:col-span-2 space-y-12">
            <div className="flex items-center gap-4 mb-10">
              <Newspaper className="w-6 h-6 text-brand-400" />
              <h2 className="text-3xl font-black text-white tracking-tight uppercase">Latest Briefings</h2>
            </div>
            
            <div className="grid gap-8">
              <AnimatePresence mode='popLayout'>
                {news.map((item, index) => (
                  <motion.div
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    key={item.id}
                    className="glass-card p-10 rounded-[2.5rem] border-beam group relative"
                  >
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                      <span className={`px-4 py-1.5 rounded-full text-[10px] font-black font-mono tracking-widest border ${
                        item.category === 'CRITICAL ALERT' ? 'bg-red-500/10 border-red-500/30 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.2)]' :
                        item.category === 'AI INSIGHT' ? 'bg-purple-500/10 border-purple-500/30 text-purple-400' :
                        'bg-brand-500/10 border-brand-500/30 text-brand-400'
                      }`}>
                        {item.category}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500 font-bold tracking-tighter uppercase">
                        RELEASE_TIMESTAMP: {item.date}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-black text-white mb-4 group-hover:text-brand-400 transition-colors tracking-tight">
                      {item.title}
                    </h3>
                    
                    <p className="text-slate-400 leading-relaxed font-light mb-8 max-w-2xl">
                      {item.summary}
                    </p>
                    
                    <button className="flex items-center gap-2 text-[10px] font-black text-brand-400 tracking-widest uppercase hover:text-white transition-all group/btn">
                      Read Full Intelligence
                      <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Events Sidebar (1/3 width) */}
          <div className="space-y-12">
            <div className="flex items-center gap-4 mb-10">
              <Calendar className="w-6 h-6 text-purple-400" />
              <h2 className="text-3xl font-black text-white tracking-tight uppercase">Symposiums</h2>
            </div>

            <div className="space-y-6">
              <AnimatePresence mode='popLayout'>
                {events.map((event, index) => (
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    key={event.id}
                    className="glass-premium p-8 rounded-[2rem] border-white/5 group hover:border-purple-500/20 transition-all"
                  >
                    <div className="flex items-center gap-2 text-purple-400 mb-4">
                      <Clock className="w-4 h-4" />
                      <span className="text-[10px] font-black font-mono tracking-widest uppercase">{event.date} • {event.time}</span>
                    </div>
                    <h4 className="text-lg font-black text-white mb-6 group-hover:text-purple-400 transition-colors leading-tight uppercase">
                      {event.title}
                    </h4>
                    <button 
                      onClick={() => handleRegisterInterest(event.title)}
                      className="w-full py-4 rounded-xl glass-card text-center text-[10px] font-black tracking-widest uppercase text-slate-400 group-hover:bg-purple-500 group-hover:text-black transition-all border border-white/10 group-hover:border-transparent hover:scale-[1.02] active:scale-[0.98]"
                    >
                      Register Interest
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntelligenceHub;
